/**
 * SDMS Result Entry — the QC Batch form's "Open SDMS" screen.
 *
 * Ported from QC-Batch-Prototype.html SCREEN 3 (#screenSdms / openSdms()).  The
 * "spreadsheet" is a STYLED HTML TABLE emulating Excel (column-letter header,
 * row numbers, sticky header, a metadata block, a field-row separator, and one
 * parameter block per run in the QC batch's sequence).  The per-test-method
 * SDMS TEMPLATE drives its columns — a new method is a config entry, not code.
 *
 * This is the RESULT-ENTRY entry point (from a saved QC batch).  It is distinct
 * from `SdmsSpreadsheet` (the Result-VALIDATION preview opened from the reporting
 * / Open-Package flow, which uses the heavy licensed Univer engine).  Here the
 * grid is a lightweight table so the entry screen is not blocked by ADR-0005.
 *
 * Scope (v1): faithful screen + template grid + editable Result cells + the full
 * ribbon.  Save / Submit / Recalculate / Rollback etc. are wired to the shared
 * notify service as "later phase" stubs until the entry SP + formula engine land.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from '../../components/notifications';
import { useConfirm, useSaveFlow } from '../../components/confirm';
import { AuditTrailPopup } from '../../components/AuditTrailButton';
import { qcBatchApi, type SdmsTestLookup, type SdmsQcBatchLookup,
         type QcCreateBody, type QcCreateResult, type QcSeqRow } from './qcBatch.api';
import { LookupCombo, type LookupData } from '../../components/LookupCombo';
import { NotesListPopup } from './SdmsProjectNotes';
import { SdmsBatchResultsPopup } from './SdmsBatchResultsPopup';
import { WorkflowStepperBar, WORKFLOW_STEPS, type StepState } from './WorkflowStepperBar';
import { mergeSdmsTemplate } from './sdmsMailMerge';
import { patchWorkbookCells } from './sdmsXlsxPatch';
import './sdmsEntry.css';

// The real spreadsheet control (Apache-2.0 Univer), shared with the Result
// Validation preview. Lazy so its chunk only loads on the blank-sheet path.
const SdmsUniverSheet = lazy(() => import('./SdmsUniverSheet').then(m => ({ default: m.SdmsUniverSheet })));
// Type-only import (no runtime cost, keeps the lazy chunk lazy) for the imperative
// handle the sheet exposes — `getEditedXlsx()` re-applies the live Univer model onto
// the loaded workbook and hands back the .xlsx bytes, which is how Save persists the
// analyst's typed results.
import type { SdmsUniverHandle } from './SdmsUniverSheet';
// QC Batch detail form (Phase 1 "QC Batch" ribbon action = open the linked batch,
// OG :9426). LAZY because QcBatchDetailPage statically imports THIS file (Open
// SDMS) — a static import back would be a cycle; a dynamic import breaks it.
const QcBatchDetailPage = lazy(() => import('./QcBatchDetailPage'));

/** One run from the QC batch's built sequence (samples + interleaved QC). */
export interface SdmsRun { code: string; qc: string; kind: 'sample' | 'qc' }

export interface SdmsEntrySeed {
  test:        string;
  method?:     string | null;
  matrix?:     string | null;
  instrument?: string | null;
  analyst?:    string | null;
  /** The minted QC Batch ID (display, e.g. QB…), or null when not yet saved. */
  qcBatchId?:  string | null;
  /** The QC batch Oid — audit source key (OG audit filters Source = QCBatchOid). */
  qcBatchOid?: string | null;
  runs:        SdmsRun[];
  /** OK→SDMS handoff: the built create payload (sequence + header). OG does NOT
   *  save an SDMS-template batch in the QC form — it hands the sequence to SDMS and
   *  the AnalyticalBatch is created (NewABID) from SDMS's Save (QCBatchController.cs
   *  :7213-7330). Present only on that handoff; null when opened on a saved batch. */
  pendingCreate?: QcCreateBody | null;
  /** Set when this seed came from the SDMS-hosted "New QC Batch" popup rather than
   *  the Analysis Queue → QC Batch walk. The docked workflow stepper describes that
   *  walk, so it stays hidden here — the user never took those steps. */
  fromSdmsPopup?: boolean;
}

/** Build a lightweight OOXML workbook from the handed-over QC sequence so the
 *  batch's samples/QC are VISIBLE in SDMS immediately (OG MailMerges the sequence
 *  into the per-test template — that template merge is a later phase; until then
 *  the sequence itself is shown). Goes through the same excelToUniver() path as
 *  every other sheet, so there's no second, unproven workbook constructor. */
async function buildSequenceWorkbook(seq: QcSeqRow[]): Promise<ArrayBuffer> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sequence');
  const cols: { header: string; width: number }[] = [
    { header: '#', width: 5 }, { header: 'QC Type', width: 14 }, { header: 'Code', width: 16 },
    { header: 'System ID', width: 15 }, { header: 'Job ID', width: 14 }, { header: 'Sample ID', width: 16 },
    { header: 'Sample Name', width: 26 }, { header: 'Component', width: 16 }, { header: 'DF', width: 8 },
    { header: 'Sample Amt', width: 12 }, { header: 'Final Vol', width: 12 }, { header: 'Units', width: 10 },
  ];
  ws.columns = cols;
  ws.getRow(1).font = { bold: true };
  seq.forEach((r, i) => ws.addRow([
    r.sort || i + 1, r.qcType, r.code, r.systemId ?? '', r.jobId ?? '', r.sampleId ?? '',
    r.sampleName ?? '', r.component ?? '', r.df, r.sampleAmount ?? '', r.finalVol ?? '', r.units ?? '',
  ]));
  // Extend the sheet's extents so it scrolls like Excel past the last data row.
  ws.getRow(Math.max(BLANK_SHEET_ROWS, seq.length + 5)).height = 15;
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

/** Extents of the BLANK worksheet handed to Univer, so the empty sheet scrolls
 *  like Excel instead of ending at the last populated cell. */
const BLANK_SHEET_COLS = 60;
const BLANK_SHEET_ROWS = 300;

/** Grid-lookup column widths, sized for readability like OG's combos (which set
 *  Method explicitly to 350 — ctrl :2174 — and leave the rest to fit). */
const SDMS_TEST_COL_W       = [190, 110, 250, 220, 70];                      // Test·Matrix·Method·TemplateName·Sx
const SDMS_BATCH_COL_W      = [160, 120, 180, 110, 150, 130];                // QCBatchID·JobID·Test·Matrix·Method·Template
const SDMS_BATCH_COL_W_VIEW = [160, 120, 180, 110, 150, 130, 120];           // …+ Status (View mode)

/** SDMS-screen island stepper: the QC-batch workflow (WORKFLOW_STEPS: Select
 *  test → … → Open SDMS) + the two SDMS-only tail steps, matching the prototype
 *  (QC-Batch-Prototype.html stepLabels(): BASE_STEPS.concat(['Save','Submit'])). */
const SDMS_STEPS = [...WORKFLOW_STEPS, 'Save', 'Submit'];

/** The blank canvas is a constant — build the empty .xlsx buffer ONCE (module
 *  scope) and reuse it across every SDMS open, so re-opening the screen never
 *  re-imports ExcelJS or rebuilds the buffer. */
let _blankBufPromise: Promise<ArrayBuffer> | null = null;
function blankWorkbook(): Promise<ArrayBuffer> {
  if (!_blankBufPromise) {
    _blankBufPromise = (async () => {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Sheet1');
      // Real extents so the empty sheet scrolls like Excel (excelToUniver sizes
      // the canvas from columnCount/rowCount; a bare addWorksheet() reports 0).
      ws.columns = Array.from({ length: BLANK_SHEET_COLS }, () => ({ width: 11 }));
      ws.getRow(BLANK_SHEET_ROWS).height = 15;
      return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
    })();
  }
  return _blankBufPromise;
}

// Warm the heavy spreadsheet chunk (Univer + ExcelJS) in the BACKGROUND as soon
// as this module is first evaluated — which happens when the Analysis/QC area
// loads (QcBatchDetailPage statically imports this file). By the time the user
// opens SDMS, the big Univer chunk is already downloaded + parsed and the blank
// buffer is built, so the sheet paints instantly on click instead of downloading
// Univer on the click itself. Idle-scheduled so it never competes with the
// initial render; failures are swallowed (it's a pure prefetch).
let _sdmsWarmStarted = false;
function warmSdmsSheet(): void {
  if (_sdmsWarmStarted || typeof window === 'undefined') return;
  _sdmsWarmStarted = true;
  const go = () => {
    import('./SdmsUniverSheet').catch(() => {});
    blankWorkbook().catch(() => {});
    // Also warm the QC Batch form chunk: Retrieve in a blank SDMS opens the
    // "New QC Batch" modal, which lazy-loads QcBatchDetailPage. Preloading it here
    // (idle, after this module finishes evaluating so the static import cycle is
    // already resolved) means the modal opens straight onto the form instead of
    // flashing the "Opening QC Batch…" Suspense fallback.
    import('./QcBatchDetailPage').catch(() => {});
  };
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (ric) ric(go, { timeout: 2000 }); else setTimeout(go, 400);
}
warmSdmsSheet();

/**
 * Rendered two ways:
 *  • from the QC Batch form's "Open SDMS" — `seed` carries the batch context and
 *    `onBack` returns to the QC detail;
 *  • from the "Analysis → SDMS" nav item — no seed (blank sheet; Retrieve a batch),
 *    no Back (it's a top-level page). Matches OG nav item id 'Spreadsheet' (view 'SDMS').
 */
export default function SdmsResultEntryPage({ seed, onBack }: { seed?: SdmsEntrySeed; onBack?: () => void }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [full, setFull] = useState(true);
  // OG Mode combo is "View" / "Enter" (Review/Verify are commented out in OG,
  // SpreadsheetEntryController :2158). Enter = data entry; View = read-only load.
  //
  // OG derives the mode from the batch's status: Enter for qcstatus 0|1, View for
  // >1 (ctrl :2612 / :2697). The enum is 0 = Pending Result Entry, 1 = Pending
  // Submission, 2 = Pending Validation, 3 = Validated, 4 = Pending Approval,
  // 5 = Approved — so ENTER covers Pending Submission too (:2685 sets the ribbon
  // Status to "Pending Submission" inside that same branch). Reopening a
  // part-entered batch in Enter to finish it is normal OG usage, NOT an edge case.
  //
  // DEFAULT = 'Enter' (changed 2026-08-10, OG parity). `qcstatus` is never assigned
  // before OG's :2612 check, so a freshly-opened view carries the int default 0 —
  // which lands OG in Enter. AI previously hardcoded nav-opened to VIEW on the
  // reasoning that Enter "lists no batches"; that reasoning was wrong. Enter's list
  // is empty ONLY while no Test is picked, because the legacy sproc's Enter branch
  // is `WHERE TM.Oid = @TestMethodID` with no null-guard (verified on
  // AlpacaDWO_260804: Enter alone 0 rows, Enter + a test returns rows, View alone
  // 4371). Note Enter hides the Audit Trail action — also OG (modechange).
  const [mode, setMode] = useState<'Enter' | 'View'>('Enter');
  // View-mode Edit toggle (OG: Edit→"Update", Clear→"Cancel" while editing).
  const [editing, setEditing] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // Unimplemented ribbon actions announce themselves in the shared MODAL (never
  // the top banner) — matching how OG surfaces such notices via ShowMessage.
  const stub = (name: string) =>
    void confirm.alert({ title: name, message: `${name} is not available yet.`, tone: 'info' });
  // Blank-sheet workbook. We hand Univer a REAL (empty) .xlsx buffer rather than a
  // hand-built Univer snapshot: that way the blank sheet goes through the exact
  // same excelToUniver() path the Result-Validation preview uses, so there's no
  // second, unproven way to construct a workbook. (A hand-rolled snapshot missing
  // fields Univer expects renders as a completely blank area — that cost two
  // failed attempts on 2026-07-28.)
  const [blankBuf, setBlankBuf] = useState<ArrayBuffer | null>(null);
  // The RETRIEVED batch workbook (its saved sheet, or its empty template) — this is
  // what makes the sheet actually fill once a QC batch is picked. Null = show the
  // blank canvas instead.
  const [batchBuf, setBatchBuf] = useState<ArrayBuffer | null>(null);
  const [retrieving, setRetrieving] = useState(false);
  // The OK→SDMS handoff batch is saved FROM here (OG NewABID). Once SDMS Save
  // creates it, these hold the minted Oid/QB so audit/results/notes light up and
  // the header shows the real QC Batch ID.
  const runSave = useSaveFlow();
  const [savedOid, setSavedOid] = useState<string | null>(null);
  const [savedQb,  setSavedQb]  = useState<string | null>(null);
  // Where the merge put each header FIELD (`Sheet!A1`). QCBATCHID is merged BLANK
  // because the id does not exist until the save mints it, so persistWorkbook
  // stamps it into this cell before upload (client 2026-08-19).
  const fieldRefsRef = useRef<Record<string, string>>({});
  // OG's ribbon Test + QCBatchID are COMBOs, not text boxes (SpreadsheetEntryController
  // :2249-2254 adds the SampleID/Test/QCBatchID combos to the SDMS tab). Cascade:
  // pick a Test → its QC batches load → pick one → Retrieve opens it.
  const [testList,  setTestList]  = useState<SdmsTestLookup[]>([]);
  const [testSel,   setTestSel]   = useState('');   // testMethodId (the real key)
  const [testText,  setTestText]  = useState('');   // what the closed combo shows
  const [testTpl,   setTestTpl]   = useState<number | null>(null); // its SDMS TemplateID
  const [batchList, setBatchList] = useState<SdmsQcBatchLookup[]>([]);
  const [batchSel,  setBatchSel]  = useState('');   // uqAnalyticalBatchID
  const [batchText, setBatchText] = useState('');

  const s: SdmsEntrySeed = seed ?? { test: '', runs: [] };
  const qb  = savedQb || s.qcBatchId || 'Autogenerate (Save to create QB)';
  // Nav-opened (no seed) → blank canvas until Retrieve; seeded → the batch's
  // workbook auto-loads. Both render the ONE Univer control (no template table).
  const blank = !seed;

  // OG enable/visibility rules (SpreadsheetEntryController modechange :16870+):
  //  • sheet editable in Enter mode, or in View mode while "Edit" is active;
  //  • Audit Trail shows in VIEW mode only (hidden in Enter);
  //  • batch-dependent actions (Submit/QC Batch/Data Package/Report/RollBack …)
  //    need a saved batch;  RollBack is View-only, Save/BindGrid are Enter/edit.
  // The batch these read-only actions target: the seeded QC batch, or the one
  // picked from the QCBatchID combo when nav-opened.
  const effBatchOid   = s.qcBatchOid || savedOid || batchSel;   // Oid (audit / detail key)
  const effBatchLabel = savedQb || s.qcBatchId || batchText || effBatchOid; // display "QB…"
  const effTest       = s.test       || testText;
  const hasBatch  = !!effBatchOid;
  const sheetEdit = mode === 'Enter' || editing;

  // Imperative handle on the Univer sheet — Save reads the edited workbook through it.
  const sheetRef = useRef<SdmsUniverHandle | null>(null);

  /** Persist the analyst's typed results: pull the edited .xlsx out of Univer and
   *  store it against the batch. Legacy's SDMS Save writes exactly this blob
   *  (`ABID.SpreadSheet = …SaveDocument(OpenXml)`) and creates NO structured result
   *  rows — those are the Submit phase — so blob-only is the faithful scope here.
   *  Returns false (having explained why) when there is nothing to persist to. */
  const persistWorkbook = async (batchOid: string, qcBatchId?: string): Promise<boolean> => {
    const edited = await sheetRef.current?.getEditedXlsx();
    if (!edited) {
      // No sheet mounted / nothing loaded — don't silently claim a save happened.
      await confirm.alert({ title: 'Save', tone: 'warning',
        message: 'There is no loaded spreadsheet to save. Retrieve a batch first.' });
      return false;
    }
    // Stamp the QC Batch ID into the cell the merge left blank. ALPACA gets away
    // without this because its SDMS screen RE-MERGES from the template on Retrieve,
    // resolving FIELD("QCBATCHID") once the id exists; we render the STORED sheet, so
    // whatever the merge missed stays missing. Surgical, like every other write here —
    // one cell, nothing else in the package touched.
    let bytes = edited.bytes;
    const qbNow = qcBatchId ?? savedQb ?? s.qcBatchId ?? '';
    const ref = fieldRefsRef.current.QCBATCHID;
    if (qbNow && ref) {
      const [sheetName, a1ref] = ref.split('!');
      const m = /^([A-Z]+)(d+)$/.exec(a1ref ?? '');
      if (sheetName && m) {
        let col = 0;
        for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
        try {
          bytes = await patchWorkbookCells(bytes, {
            [sheetName]: [{ row: parseInt(m[2]!, 10) - 1, col: col - 1, value: qbNow }],
          });
        } catch (e) {
          // Non-fatal: the sheet still saves, the header cell just stays blank.
          console.warn('[SDMS] QCBATCHID stamp failed', e);
        }
      }
    }
    await qcBatchApi.saveSdmsEntryWorkbook(batchOid, bytes);
    return true;
  };

  // Save (Enter mode) — OG NewABID: the AnalyticalBatch is created FROM SDMS. The
  // QC Batch form handed over the built payload WITHOUT saving (OG defers the save
  // here), so this is where it persists — via the SAME race-safe create the
  // plain-batch OK uses. Shared save flow: confirm → audited create → milestone
  // success (minted QC Batch ID). Nav-opened Enter has no staged batch, so Save
  // there stays a stub until Enter-mode result entry lands.
  const doSave = () => {
    const body = seed?.pendingCreate;

    // ── Case B: the batch already exists (nav-opened + Retrieved, or a second Save
    // after the create below). Persist the typed results onto it. This is what makes
    // Result Entry actually retain work — previously this path was a stub, so every
    // typed value was lost on leaving the screen.
    if (!body || savedOid) {
      const target = savedOid || effBatchOid;
      if (!target) { stub('Save'); return; }   // nothing loaded at all
      void runSave({
        save: async () => { await persistWorkbook(target); },
        successDialog: () => ({
          type: 'success',
          title: 'Results Saved',
          message: 'The spreadsheet has been saved to this QC batch.',
          rows: [
            { label: 'QC Batch', value: effBatchLabel || '—' },
            { label: 'Test',     value: effTest || '—' },
          ],
          note: 'Reopening this batch will load your entered results.',
        }),
        formName: 'SDMS Result Entry',
        auditAction: 'edit',
      });
      return;
    }

    // ── Case A: OG NewABID — the AnalyticalBatch is created FROM SDMS. The QC Batch
    // form handed over the built payload WITHOUT saving (OG defers the save here), so
    // create it via the SAME race-safe create the plain-batch OK uses, THEN persist
    // the workbook onto the batch it just minted (one user action = one Save).
    let minted: QcCreateResult | null = null;
    let sheetSaved = false;
    void runSave({
      save: async () => {
        const r = await qcBatchApi.create(body);
        minted = r; setSavedOid(r.abOid); setSavedQb(r.qcBatchId);
        // Best-effort: the batch is already committed, so a workbook failure must not
        // present as "nothing saved". Reported honestly in the dialog note below.
        try { sheetSaved = await persistWorkbook(r.abOid, r.qcBatchId); } catch { sheetSaved = false; }
      },
      successDialog: () => ({
        type: 'success',
        title: 'QC Batch Created',
        idLabel: 'QC Batch ID',
        idValue: minted?.qcBatchId ?? '',
        rows: [
          { label: 'Test', value: effTest || '—' },
          { label: 'Runs', value: String(minted?.seqCount ?? body.sequence.length) },
          { label: 'Samples linked', value: String(minted?.samplesLinked ?? 0) },
          { label: 'Spreadsheet', value: sheetSaved ? 'Saved' : 'Not saved' },
        ],
        note: sheetSaved
          ? 'The batch and your spreadsheet are saved. You can now enter and submit results.'
          : 'The batch is saved, but the spreadsheet could not be stored — press Save again.',
      }),
      formName: 'QC Batches',
      auditAction: 'create',
    });
  };

  // "Update" — the View-mode Edit toggle's SECOND click (OG :5667, the `Isedit ==
  // true` branch). OG does: BindSampleToGrid (read the sheet back), write
  // `qC.SpreadSheet = SaveDocument(...)`, re-read the batch's SpreadSheetEntry rows,
  // `ABCreateUpdate(qC, spreadSheets)` to persist the values, CommitChanges, set the
  // sheet ReadOnly again, then show "Updatesuccess".
  //
  // AI's equivalent of that whole middle section is `persistWorkbook` — the PUT
  // routes through SaveSdmsResultEditsAsync, which writes the typed values back to
  // SpreadSheetEntry_SampleInfo and stores the workbook. So Update = the same
  // persistence Save/Case-B already does, PLUS leaving edit mode on success.
  //
  // Previously this button was `setEditing(e => !e)` — "Update" just dropped out of
  // edit mode and DISCARDED the edits, the exact opposite of what it says. (Its
  // Cancel twin was fixed on 2026-08-10; this closes the pair.)
  const doUpdate = () => {
    const target = savedOid || effBatchOid;
    if (!target) {                     // OG "NotDataLoaded"
      void confirm.alert({ title: 'Update', tone: 'warning',
        message: 'There is no loaded spreadsheet to update. Retrieve a batch first.' });
      return;
    }
    void runSave({
      save: async () => { await persistWorkbook(target); },
      successDialog: () => ({
        type: 'success',
        title: 'Updated',
        message: 'The spreadsheet has been updated on this QC batch.',
        rows: [
          { label: 'QC Batch', value: effBatchLabel || '—' },
          { label: 'Test',     value: effTest || '—' },
        ],
      }),
      // Leave edit mode ONLY once the write succeeded (OG sets ReadOnly=true at the
      // same point). A failed save keeps the sheet editable, so the analyst's work
      // is never stranded behind a read-only view.
      onSaved: () => setEditing(false),
      formName: 'SDMS Result Entry',
      auditAction: 'edit',
    });
  };

  // Docked island stepper (issue: it vanished when SDMS opened because the QC
  // page — which rendered the pill — unmounts on the full-page swap). The QC-batch
  // steps are DONE when we arrived through the Analysis Queue → QC Batch flow
  // (seeded), then Save → Submit.
  //
  // Only consumed by <WorkflowStepperBar>, which as of 2026-08-10 renders for
  // SEEDED opens only — so this always describes the "came through the QC flow"
  // case. (Supersedes the 2026-07-06 requirement that a DIRECT nav open start a
  // FRESH pill with Open SDMS active: that pill showed five create-steps as "todo"
  // for a flow the user is not in, so a nav-opened SDMS now shows no stepper.)
  const stepStatus = useMemo<StepState[]>(() => {
    const saved = !!(savedOid || s.qcBatchOid);
    return SDMS_STEPS.map((_, i) =>
      i < WORKFLOW_STEPS.length ? 'done'
      : i === WORKFLOW_STEPS.length ? (saved ? 'done' : 'active')   // Save
      : (saved ? 'active' : 'todo'));                               // Submit
  }, [savedOid, s.qcBatchOid]);

  // Phase 1 read-only overlays (Project Notes · QC Batch Results · open the QC
  // Batch form). Each needs a loaded batch.
  const [notesOpen,    setNotesOpen]    = useState(false);
  const [resultsOpen,  setResultsOpen]  = useState(false);
  const [qcDetailOpen, setQcDetailOpen] = useState(false);
  // "Create a QC batch from here" — OG SpreadsheetEntryController :5507: in ENTER
  // mode with NO batch picked, Retrieve pops the `QCBatchsequence` dashboard as a
  // MODAL so the batch can be built without leaving SDMS. `handedSeed` is what that
  // popup gives back (its Ok → SDMS handoff); rendering a seeded SDMS with it is
  // how the created batch takes over this screen.
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [handedSeed,   setHandedSeed]   = useState<SdmsEntrySeed | null>(null);

  useEffect(() => {
    // Always have the blank canvas ready — it's the fallback surface for BOTH
    // paths (nav-opened before Retrieve, and a seeded batch with no sheet on file).
    if (blankBuf) return;
    let cancelled = false;
    // Kick off the Univer chunk download IN PARALLEL with the blank buffer. Univer
    // is the single biggest load; it used to start only AFTER the buffer existed
    // (<SdmsUniverSheet> isn't mounted until a buf is ready), so the two heavy
    // steps ran back-to-back. Prefetching here overlaps them → the sheet paints
    // sooner. (No-op if the module warm-up already started it.)
    import('./SdmsUniverSheet').catch(() => {});
    blankWorkbook()
      .then(out => { if (!cancelled) setBlankBuf(out); })
      .catch(e => {
        console.warn('[SDMS] blank workbook build failed', e);
        if (!cancelled) notify.error('Could not open a blank spreadsheet.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blank]);

  // Full screen must hide the app's left navigation too (rail + sidebar), which
  // live OUTSIDE this component in AppShell — so drive it with a body class that
  // shell.css keys off. Cleaned up on unmount/toggle so leaving the screen (or
  // navigating away while full-screen) always restores the nav.
  useEffect(() => {
    document.body.classList.toggle('sdms-fullscreen', full);
    return () => document.body.classList.remove('sdms-fullscreen');
  }, [full]);

  // Test combo — the real SDMS feed (AI_Analysis_Sdms_Tests_SP): only tests that are
  // SDMS-CONFIGURED (have a SpreadSheetBuilder_TestParameter row) AND currently have
  // pending samples, with OG's per-mode criteria. Re-fetches when Mode flips, because
  // Enter and View count different samples.
  useEffect(() => {
    if (!blank) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await qcBatchApi.sdmsTests(mode);
        if (!cancelled) setTestList(rows);
      } catch { if (!cancelled) notify.error('Could not load the test list.'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blank, mode]);

  // Grid-popup data. <LookupCombo> treats column 0 as the value it displays, and
  // hands the WHOLE row back on pick — so the real key (TestMethodId / AbOid) is
  // resolved through a row→id map instead of being shown as a GUID column.
  const ROW_KEY = (r: string[]) => r.join(' ');
  // OG Test columns: Test · Matrix · Method · TemplateName · Sx (ctrl :2167-2171).
  const testRow = (t: SdmsTestLookup) =>
    [t.test ?? '', t.matrix ?? '', t.method ?? '', t.templateName ?? '', String(t.sx)];
  const testLookup = useMemo<LookupData>(() => ({
    cols: ['Test', 'Matrix', 'Method', 'TemplateName', 'Sx'],
    rows: testList.map(testRow),
  }), [testList]);
  const testIdByRow = useMemo(
    () => new Map(testList.map(t => [ROW_KEY(testRow(t)), t.testMethodId])), [testList]);
  // The picked test's SDMS TemplateID — the lookup already carries it. Needed by the
  // "create a batch from here" popup: the QC form decides SDMS-vs-plain-batch on
  // templateId, so seeding it keeps that form on the SDMS path from the first render.
  const testTplByRow = useMemo(
    () => new Map(testList.map(t => [ROW_KEY(testRow(t)), t.templateID])), [testList]);
  // The full selected-test row — carries Matrix + Method, which the ribbon otherwise
  // drops (it only keeps testSel/testText/testTpl). The New QC Batch form seeds
  // effMatrix/effMethod straight from seed.matrix/seed.method when a testMethodId is
  // present, so without these the Matrix and Method fields open blank. (testMethodId
  // is unique per Test×Method×Matrix, so the match is unambiguous.)
  const selectedTestRow = useMemo(
    () => testList.find(t => t.testMethodId === testSel) ?? null, [testList, testSel]);

  // OG QCBatchID columns: JobID · Test · Matrix · Method · Template · QCBatchID ·
  // Status (ctrl :2184-2195). Status is VIEW-mode only (:16608 hides it in Enter,
  // :16639 shows it in View).
  // DEVIATION (deliberate): OG lists JobID first and shows the ABID in the CLOSED
  // combo via DevExpress `TextFormatString = "{5}"`. <LookupCombo> always displays
  // column 0, so QCBatchID leads here — the same COLUMN SET as OG, reordered so the
  // QCBatchID field shows a QC batch ID instead of a JobID.
  const batchRow = (b: SdmsQcBatchLookup) => {
    const base = [b.abid ?? '', b.jobID ?? '', b.test ?? '', b.matrix ?? '',
                  b.method ?? '', b.template ?? ''];
    return mode === 'View' ? [...base, b.status ?? ''] : base;
  };
  const batchLookup = useMemo<LookupData>(() => ({
    cols: mode === 'View'
      ? ['QCBatchID', 'JobID', 'Test', 'Matrix', 'Method', 'Template', 'Status']
      : ['QCBatchID', 'JobID', 'Test', 'Matrix', 'Method', 'Template'],
    rows: batchList.map(batchRow),
  }), [batchList, mode]);
  const batchIdByRow = useMemo(
    () => new Map(batchList.map(b => [ROW_KEY(batchRow(b)), b.uqAnalyticalBatchID])),
    [batchList, mode]);

  // QCBatchID options — from OG's own sproc, so the grid shows exactly its rows.
  // Re-runs on a Mode change too, which alters both the row set and whether Status
  // is a column.
  //
  // OG fills this list DIFFERENTLY per mode (ctrl :16608 vs :16702):
  //   Enter → EXEC … @Mode, @TestMethodID  — a Test is mandatory (the legacy
  //           sproc's Enter branch has no null-guard on it), so cascade from Test.
  //   View  → EXEC … @Mode                 — NO test filter: every test's batches.
  // So only Enter waits for a Test; View loads straight away and the grid's Test
  // column tells the rows apart. (Previously View was gated on a Test too, which
  // made it strictly narrower than OG.)
  useEffect(() => {
    // Changing Test (or Mode) invalidates the picked batch AND its loaded sheet.
    setBatchSel(''); setBatchText(''); setBatchBuf(null);
    if (!testSel && mode !== 'View') { setBatchList([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const rows = await qcBatchApi.sdmsQcBatches(testSel || undefined, mode);
        if (!cancelled) setBatchList(rows);
      } catch { if (!cancelled) setBatchList([]); }
    })();
    return () => { cancelled = true; };
  }, [testSel, mode]);

  // Loads the selected batch's sheet — invoked by RETRIEVE only (OG :5505: the
  // AnalyticalBatch combo has no auto-load; Retrieve reads the batch's SpreadSheet,
  // else its empty template). Endpoint serves the SAVED workbook, else the
  // template, else 204.
  const loadBatchSheet = async (abOid: string) => {
    // The overlay stays up from here until the sheet has PAINTED — on success
    // it's cleared by <SdmsUniverSheet onReady>, NOT here, so there's no white
    // flash between "fetch done" and "sheet rendered". Failure paths clear it.
    setRetrieving(true);
    try {
      const wb = await qcBatchApi.sdmsWorkbook(abOid);
      if (!wb) {
        setBatchBuf(null);
        setRetrieving(false);
        // A genuine "nothing to show" condition — OG shows the same kind of
        // notice (e.g. "Data Package is not available…") via a modal.
        notify.warning('No SDMS sheet on file for this batch yet.');
        return;
      }
      // OG does NOT announce a successful load — the sheet appearing IS the
      // feedback. onReady clears `retrieving` once Univer has painted.
      setBatchBuf(wb.buf);
    } catch {
      setBatchBuf(null);
      setRetrieving(false);
      notify.error('Could not load the SDMS sheet for this batch.');
    }
  };

  // Clear / Cancel — OG SpreadsheetEntryController :5646. ONE button with TWO
  // behaviours, chosen by edit state, and its label/enablement follow suit:
  //
  //   Enter mode           → "Clear"  · ENABLED  (modechange :16911)
  //        confirm "All Unssaved changes will be cleared. Do you want to continue?"
  //        (OG's verbatim wording, typo and all — kept, minus the typo) then drop
  //        the loaded sheet. The Test/QCBatchID picks are NOT reset: OG clears the
  //        sheet, not the ribbon, so Retrieve can reload the same batch.
  //   View + editing       → "Cancel" · ENABLED  (:17053-17055 renames it and swaps
  //        the icon). Discards unsaved edits by RELOADING the sheet from the saved
  //        batch — OG `LoadfromAB(sheet, batch)` + ABGridrefresh + CalibRefresh —
  //        then leaves edit mode. Previously AI only did `setEditing(false)`, which
  //        left the user's unsaved edits sitting in the sheet: the "cancel" did not
  //        actually cancel anything.
  //   View, not editing    → DISABLED (:17070).
  //
  // The confirm goes through the shared dialog (standing rule 7), not a native
  // confirm() as OG uses.
  const doClear = async () => {
    if (editing) {
      setEditing(false);
      if (effBatchOid) await loadBatchSheet(effBatchOid);   // re-read = discard edits
      return;
    }
    const ok = await confirm.confirm({
      title: 'Clear',
      message: 'All unsaved changes will be cleared. Do you want to continue?',
      tone: 'danger',
    });
    if (!ok) return;
    setBatchBuf(null);
  };

  // Seeded entry (Open SDMS button / the OK→SDMS handoff) — auto-load the batch's
  // workbook on mount (its saved sheet, else its empty template), so the analyst
  // lands directly on the sheet. Nav-opened has no seed → skipped (Retrieve loads).
  useEffect(() => {
    if (blank || !s.qcBatchOid) return;
    void loadBatchSheet(s.qcBatchOid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OK→SDMS handoff — the batch is NOT saved yet (OG defers the save to SDMS), so
  // there's no server workbook to fetch. Render the handed-over sequence itself as
  // the sheet so the batch's samples/QC are visible immediately (the per-template
  // MailMerge OG does is a later phase). Save (below) then persists the batch.
  useEffect(() => {
    const pc = seed?.pendingCreate;
    if (!pc || !pc.sequence?.length) return;
    setRetrieving(true);
    let cancelled = false;
    (async () => {
      try {
        // OG-faithful: mail-merge the per-test template with the built sequence.
        // The backend returns the template + records; the merge runs client-side
        // (ExcelJS) so the result stays Univer-readable. A failure (no template
        // blob, or an unsupported template feature) degrades to the raw-sequence
        // sheet rather than erroring — the batch stays visible either way.
        if (pc.templateId != null) {
          try {
            const md = await qcBatchApi.sdmsMergeData({
              templateId: pc.templateId, testMethodId: pc.testMethodId, sequence: pc.sequence,
            });
            if (md) {
              const d = new Date();
              const p2 = (n: number) => String(n).padStart(2, '0');
              const nowStr = `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
              const merged = await mergeSdmsTemplate(md.template, {
                TEST: s.test || '', METHOD: s.method ?? '', MATRIX: s.matrix ?? '',
                INSTRUMENT: s.instrument ?? '', QCBATCHID: savedQb ?? s.qcBatchId ?? '',   // blank until saved (OG parity)
                ENTEREDBY: s.analyst ?? 'Service', ANALYZEDBY: s.analyst ?? 'Service',
                ENTEREDDATE: nowStr, ANALYZEDDATE: nowStr,
              }, md.records, fieldRefsRef.current);
              if (!cancelled) setBatchBuf(merged); return;   // onReady clears retrieving
            }
          } catch (mergeErr) {
            console.warn('[SDMS] template merge failed — falling back to the sequence table', mergeErr);
          }
        }
        const buf = await buildSequenceWorkbook(pc.sequence);
        if (!cancelled) setBatchBuf(buf);
      } catch (e) {
        console.warn('[SDMS] merge/sequence build failed', e);
        if (!cancelled) { setRetrieving(false); notify.error('Could not render the batch sheet.'); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The popup handed us a built batch (OG :5507 flow completed) — hand this screen
  // over to a SEEDED SDMS for it. Re-rendering ourselves with the seed reuses the
  // exact same path as the Analysis Queue → QC Batch → Ok handoff, rather than
  // teaching this instance to adopt a seed mid-life. Back returns to the blank
  // nav-opened screen. (No infinite recursion: the seeded instance has blank=false,
  // so its Retrieve never reaches the create branch.)
  if (handedSeed) {
    return <SdmsResultEntryPage seed={handedSeed} onBack={() => setHandedSeed(null)} />;
  }

  // "QC Batch" ribbon action — open the linked QC Batch detail form (view mode),
  // OG's QCBatchDetail (:9426). Full-page swap; Back returns to SDMS. Rendered
  // lazily to break the QcBatchDetailPage↔this-file import cycle.
  if (qcDetailOpen && effBatchOid) {
    return (
      <Suspense fallback={<div className="xl-loading" style={{ padding: 24 }}>Opening QC Batch…</div>}>
        <QcBatchDetailPage
          seed={{
            testMethodId: null, test: effTest || null, method: s.method ?? null,
            matrix: s.matrix ?? null, templateID: null,
            existingAbOid: effBatchOid, viewOnly: true,
          }}
          onBack={() => setQcDetailOpen(false)}
        />
      </Suspense>
    );
  }

  return (
    <div className={`sdms-root${full ? ' sdmsfull' : ''}`}>
      {/* Docked "Dynamic Island" workflow stepper (BO UI Standard §5) — portals
          into the AppShell topbar slot so it stays visible on the SDMS screen too
          (incl. full-screen, where the topbar is kept).
          SEEDED OPENS ONLY (2026-08-10): the steps describe the QC-batch creation
          flow (Select test → … → Open SDMS → Save → Submit), which the user is
          actually walking when they arrive via Analysis Queue → QC Batch → Ok.
          Opening SDMS straight from the navigation is not that flow — the batch
          already exists and is picked from the ribbon — so a pill showing five
          "todo" create-steps would describe work that never happens. Hidden there,
          and equally for a batch built through the SDMS-hosted "New QC Batch" popup
          (`fromSdmsPopup`): that user never walked the queue steps either. */}
      {!blank && !s.fromSdmsPopup && <WorkflowStepperBar stepStatus={stepStatus} steps={SDMS_STEPS} />}
      {onBack && (
        <div className="sdms-back">
          <button className="btn" onClick={onBack}>‹ Back to QC Batch</button>
        </div>
      )}

      <div className="crumb">
        {onBack ? <a onClick={onBack}>QC Batch</a> : <span>Analysis</span>} / <b>SDMS</b>
      </div>
      <div className="pagehead"><h1>SDMS — Result Entry · {qb}</h1></div>
      <div className="sub">
        Spreadsheet result entry rendered from the test's <b>SDMS template</b> — columns, header and
        formulas come from the template builder, so every test method shows its own layout. New methods
        are a config entry, not new code.
      </div>

      {/* Excel tabs — only the SDMS tab. The File / Home / Insert / … tabs were
          decorative (no handlers) and are removed per request. */}
      <div className="xltabs">
        <span className="xt on">SDMS</span>
      </div>

      {/* Excel ribbon */}
      <div className="sdms-rib">
        <div className="grp">
          <div className="fld2">Mode
            <select value={mode} onChange={e => { setMode(e.target.value as 'Enter' | 'View'); setEditing(false); }}>
              <option value="View">View</option>
              <option value="Enter">Enter</option>
            </select>
          </div>
          {/* Test + QCBatchID are XAF GRID lookups in OG — a dropdown containing a
              multi-COLUMN grid, not a plain list. Reuses the shared <LookupCombo>
              (promoted to components/ for this). When opened FROM a QC Batch the
              pair is fixed by the seed, so the trigger just shows that value. */}
          <div className="fld2">Test
            {seed
              ? <input value={s.test} readOnly />
              : <LookupCombo lookup={testLookup} value={testText}
                  placeholder="Select test…" className="sdms-lk"
                  showFilter={false} wrapCells popupWidth={860}
                  colWidths={SDMS_TEST_COL_W}
                  onPick={row => {
                    setTestSel(testIdByRow.get(ROW_KEY(row)) ?? '');
                    setTestText(row[0] ?? '');
                    setTestTpl(testTplByRow.get(ROW_KEY(row)) ?? null);
                  }} />}
          </div>
          <div className="fld2">QCBatchID
            {seed
              ? <input value={qb} readOnly />
              : <LookupCombo lookup={batchLookup} value={batchText}
                  placeholder={testSel || mode === 'View' ? 'Select QC batch…' : 'Select a test first'}
                  className="sdms-lk"
                  showFilter={false} wrapCells
                  popupWidth={mode === 'View' ? 980 : 860}
                  colWidths={mode === 'View' ? SDMS_BATCH_COL_W_VIEW : SDMS_BATCH_COL_W}
                  // Enter mode is NOT blocked. An earlier hint here told the user to
                  // "switch Mode to View to open an existing QC batch" because Enter
                  // "lists no batches" — that was wrong and made the batch unpickable
                  // in Enter at all. OG opens batches in Enter routinely: Enter covers
                  // qcstatus 0|1, i.e. Pending Result Entry AND **Pending Submission**
                  // (ctrl :2612/:2685), which is how a part-entered batch is reopened
                  // to finish it. Enter's list is only ever empty when no Test is
                  // picked, because the legacy sproc's Enter branch has no null-guard
                  // on @TestMethodID. (Fixed 2026-08-10.)
                  blockedReason={
                    mode === 'Enter' && !testSel
                      ? 'Pick a Test first — in Enter mode the QC batch list is per-test.'
                      : undefined}
                  onBlocked={r => notify.warning(r)}
                  onPick={row => {
                    // Picking a QCBatchID only SELECTS it — OG's AnalyticalBatch
                    // combo has no SelectedIndexChanged (only the Test combo does,
                    // ctrl :2181); the sheet loads on RETRIEVE, not on select.
                    setBatchSel(batchIdByRow.get(ROW_KEY(row)) ?? ''); setBatchText(row[0] ?? '');
                    setBatchBuf(null);   // clear any previously-loaded sheet
                  }} />}
          </div>
        </div>
        <div className="grp">
          {/* OG: Retrieve (re)loads the batch chosen in the Test/QCBatchID combos.
              Picking a batch already loads it; this re-reads it from the server. */}
          <button className="rbtn" disabled={editing || retrieving} onClick={() => {
            if (!blank) { stub('Retrieve'); return; }
            // OG :5507 — Enter mode + NO batch picked = "build one from here":
            // Retrieve opens the QC Batch sequence form as a modal instead of
            // loading. (OG also requires SDMSIsWrite; AI has no SDMS permission
            // wiring yet, so that gate is not reproduced — see the change log.)
            if (mode === 'Enter' && !batchSel) { setNewBatchOpen(true); return; }
            if (!batchSel) { notify.warning('Pick a QC Batch first.'); return; }
            void loadBatchSheet(batchSel);
          }}><span className="gi">🔍</span>Retrieve</button>
          <button className="rbtn" disabled={mode !== 'Enter' || !hasBatch} onClick={() => stub('Submit')}><span className="gi">⬆️</span>Submit</button>
          <button className="rbtn" disabled={mode !== 'Enter' || !!savedOid} onClick={doSave} title="Creates the QC Batch ID"><span className="gi">💾</span>Save</button>
        </div>
        <div className="grp">
          <button className="rbtn" disabled={mode !== 'Enter'} onClick={() => stub('New Calibration')}><span className="gi">📄</span>New<br />Calibration</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => stub('Attach Calibration')}><span className="gi">📎</span>Attach<br />Calibration</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => setQcDetailOpen(true)}><span className="gi">🧪</span>QC<br />Batch</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => setResultsOpen(true)}><span className="gi">✅</span>QC Batch<br />Results</button>
        </div>
        <div className="grp">
          {/* RollBack — View mode + saved batch (OG :16879 disabled in Enter). */}
          <button className="rbtn" disabled={mode !== 'View' || !hasBatch || editing} onClick={() => stub('Rollback')}><span className="gi">↩️</span>RollBack</button>
          <button className="rbtn" disabled={!hasBatch || (mode === 'View' && !editing)} onClick={() => stub('Delete')}><span className="gi">❌</span>Delete</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => stub('Data Package')}><span className="gi">📦</span>Data<br />Package</button>
          {/* Clear ↔ Cancel while editing (OG :5646 handler, :17046/:16911 enable). */}
          <button className="rbtn" disabled={mode !== 'Enter' && !editing} onClick={() => void doClear()}>
            <span className="gi">🧽</span>{editing ? 'Cancel' : 'Clear'}</button>
          {/* Edit ↔ Update — View mode toggles the sheet editable (OG :5648). */}
          {/* Edit ↔ Update — first click makes the sheet editable, second PERSISTS
              (OG :5667; Update is not a plain toggle). Cancel is the discard path. */}
          <button className={`rbtn${editing ? ' active' : ''}`} disabled={mode !== 'View' || !hasBatch}
            onClick={() => { if (editing) doUpdate(); else setEditing(true); }}
          ><span className="gi">✏️</span>{editing ? 'Update' : 'Edit'}</button>
          <button className="rbtn" disabled={!sheetEdit} onClick={() => stub('Bind Grid')}><span className="gi">🔗</span>BindGrid</button>
        </div>
        <div className="grp">
          <button className="rbtn" onClick={() => stub('Recalculate')}><span className="gi">🧮</span>Recalculate</button>
          <button className="rbtn" disabled={!sheetEdit} onClick={() => stub('Import Files')}><span className="gi">📥</span>Import<br />Files</button>
          <button className="rbtn" onClick={() => stub('Apply Rounding')}><span className="gi">🎯</span>Apply<br />Rounding</button>
        </div>
        <div className="grp">
          <button className="rbtn" disabled={!hasBatch} onClick={() => stub('Report')}><span className="gi">📝</span>Report</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => setNotesOpen(true)}><span className="gi">🔔</span>Project<br />Notes</button>
          <button className="rbtn" disabled={!sheetEdit} onClick={() => stub('Qualifiers')}><span className="gi">☑️</span>Qualifiers</button>
          {/* Audit Trail — OG shows it in VIEW mode only (hidden in Enter, :16883). */}
          {mode === 'View' && (
            <button className="rbtn" disabled={!effBatchOid} onClick={() => setAuditOpen(true)}
              title={effBatchOid ? 'QC batch audit log' : 'Save the batch to view its audit trail'}>
              <span className="gi">📜</span>Audit<br />Trail</button>
          )}
        </div>
        <div className="grp">
          {/* OG keeps the caption "Full Screen" and just shows it as ACTIVE while
              full-screen is on (it does not switch to "Exit Screen"). */}
          <button className={`rbtn${full ? ' active' : ''}`} onClick={() => setFull(f => !f)}
            title={full ? 'Leave full screen' : 'Full screen'} aria-pressed={full}>
            <span className="gi">⛶</span>Full<br />Screen
          </button>
        </div>
      </div>

      {/* No batch loaded → the REAL spreadsheet control (shared with the Result
          Validation preview), so columns/rows keep coming as you scroll, exactly
          like OG's blank SDMS sheet. Lazy + Suspense because Univer is a heavy
          chunk we don't want on the QC-Batch path. */}
      {/* ONE spreadsheet surface for both entry points — the real Univer control.
          Nav-opened: a blank canvas until Retrieve loads a batch. Seeded (Open
          SDMS / OK→SDMS handoff): the batch's workbook auto-loads on mount (see
          the seed-load effect). The grid stays MOUNTED across a workbook swap —
          SdmsUniverSheet reloads in place on a `buf` change (no key remount,
          which flashed white); a scrim overlays it until the new sheet has
          painted (onReady). */}
      <div className="xlwrap xluniver">
        {(batchBuf ?? blankBuf) ? (
          <Suspense fallback={<div className="xl-loading">Loading spreadsheet…</div>}>
            <SdmsUniverSheet ref={sheetRef} buf={(batchBuf ?? blankBuf)!} editable={sheetEdit}
              /* Lift the "Loading the batch sheet…" scrim only once the BATCH sheet has
                 painted — NOT the empty blankBuf placeholder that mounts first (so Univer
                 inits in parallel with the merge fetch). Clearing on the blank paint was
                 the "empty sheet for a few seconds, then data" flash: the scrim lifted
                 while blankBuf was showing, before batchBuf arrived. batchBuf is always
                 set on every retrieving path, so the scrim still clears. */
              onReady={() => { if (batchBuf) setRetrieving(false); }}
              /* OG's SDMS shows a formula bar above the grid (the selected cell's
                 formula, e.g. =IF(OR(Y7="",Z7=""),"",VALUE(Y7)&" - "&VALUE(Z7))).
                 Opt-in here only — the Result-Validation preview stays chrome-free. */
              formulaBar limsRound />
          </Suspense>
        ) : <div className="xl-loading">Loading spreadsheet…</div>}
        {retrieving && (
          <div className="sdms-sheet-loading"><span>Loading the batch sheet…</span></div>
        )}
      </div>

      {/* Audit Trail — OG opens AuditData filtered FormName='QC Batches',
          Source = the QC batch Oid (SpreadsheetEntryController :9444). */}
      {auditOpen && effBatchOid && (
        <AuditTrailPopup formName="QC Batches" sourceId={effBatchOid} onClose={() => setAuditOpen(false)} />
      )}

      {/* Project Notes — the SAME batch-scoped notes popup the Result-Validation
          flow uses (OG "Project Notes", :9xxx). Reused, not reinvented. */}
      {notesOpen && effBatchOid && (
        <NotesListPopup
          batchId={effBatchOid}
          batchTest={effTest}
          batchLabel={effBatchLabel}
          businessObject="SDMS Result Entry"
          onClose={() => setNotesOpen(false)}
        />
      )}

      {/* QC Batch Results — read-only (sample × analyte) grid from the same
          batch-results endpoint the Result-Validation package view reads
          (OG QCbatchIDResults → SDMSDCAB_ListView_Copy, :9301). */}
      {resultsOpen && effBatchOid && (
        <SdmsBatchResultsPopup
          batchOid={effBatchOid}
          batchLabel={effBatchLabel}
          onClose={() => setResultsOpen(false)}
        />
      )}

      {/* Create-a-QC-batch-from-SDMS modal (OG :5507 — the `QCBatchsequence`
          dashboard shown as a PopupWindow/NewModalWindow). Same QC Batch sequence
          form the Analysis Queue uses, just hosted in a dialog; its Ok hands the
          built seed back through `onSdmsSeed` instead of swapping in its own SDMS.
          NOTE: this is a deliberate deviation from standing rule 12 (New/Edit opens
          a full-page detail) — a popup is what OG does here and what was asked for. */}
      {newBatchOpen && (
        <div className="sdms-brp-scrim"
             onMouseDown={e => { if (e.target === e.currentTarget) setNewBatchOpen(false); }}>
          <div className="sdms-brp sdms-newqc" role="dialog" aria-label="New QC Batch">
            <div className="sdms-brp-hd">
              <span>New QC Batch{testText ? ` — ${testText}` : ''}</span>
              <button className="sdms-brp-x" onClick={() => setNewBatchOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="sdms-brp-body">
              <Suspense fallback={<div className="xl-loading" style={{ padding: 24 }}>Opening QC Batch…</div>}>
                <QcBatchDetailPage
                  seed={{
                    // A Test picked in the ribbon pre-scopes the form (OG keeps
                    // OidTestMethod in that case and only clears it when no Test
                    // was chosen, :5509-5517). With none, the form opens on its own
                    // Matrix → Test → Method cascade, exactly like "New" from the
                    // QC Batches list.
                    testMethodId: testSel || null,
                    test:         testText || null,
                    // Matrix + Method come from the picked test row (the ribbon keeps
                    // only testSel/text/tpl); the form seeds them directly when a
                    // testMethodId is present, so passing null left both fields blank.
                    method:       selectedTestRow?.method ?? null,
                    matrix:       selectedTestRow?.matrix ?? null,
                    // The ribbon's Test lookup already carries its SDMS TemplateID —
                    // pass it through so the QC form is on the SDMS path from the
                    // start. (It decides SDMS-vs-plain-batch on templateId; with a
                    // null it would fall through to the plain "Do you want to Save?"
                    // path instead of handing back here.)
                    templateID:   testTpl,
                  }}
                  embedded
                  onBack={() => setNewBatchOpen(false)}
                  onSdmsSeed={hs => { setNewBatchOpen(false); setHandedSeed({ ...hs, fromSdmsPopup: true }); }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
